import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
  RouterProvider,
} from '@tanstack/react-router';
import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { AppLayout } from './components/AppLayout';
import { api, ApiError, type Me } from './lib/api';
import { useT } from './lib/i18n';
import { installErrorReporting, reportError } from './lib/report-errors';
import { ForgotPasswordPage, InvitationPage, ResetPasswordPage } from './routes/account';
import { LoginPage, SignupPage, TrialStartedPage } from './routes/auth';
import { AgendaPage, validateAgendaSearch } from './routes/agenda';
import { BookingSettingsPage } from './routes/booking-settings';
import { ClientsPage } from './routes/clients';
import { LocationsPage } from './routes/locations';
import { MessagesPage } from './routes/messages-settings';
import { SalonHubPage, StartPage } from './routes/salon-pages';
import { TodayPage } from './routes/today';
import { WelcomePage } from './routes/welcome';
import { AdminPage } from './routes/admin';
import { PrivacyPage } from './routes/privacy';
import { MyAccountPage } from './routes/my-account';
import { BusinessPage } from './routes/setup-business';
import { HoursPage } from './routes/setup-hours';
import { ServicesPage } from './routes/setup-services';
import { TeamPage } from './routes/setup-team';
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 2 } },
});

/** Current user and their salons, or null when logged out. */
async function loadMe(qc: QueryClient): Promise<Me | null> {
  try {
    return await qc.ensureQueryData({ queryKey: ['me'], queryFn: () => api<Me>('/me') });
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return null;
    throw e;
  }
}

const root = createRootRouteWithContext<{ queryClient: QueryClient }>()({ component: () => <Outlet /> });

const index = createRoute({
  getParentRoute: () => root,
  path: '/',
  beforeLoad: async ({ context }) => {
    const me = await loadMe(context.queryClient);
    if (!me) throw redirect({ to: '/inloggen' });
    // Support sessions are not "your" salon.
    const first = me.salons.find((s) => !s.support);
    if (!first) throw redirect({ to: me.isAdmin ? '/beheer' : '/start' });
    throw redirect({ to: '/s/$orgId/vandaag', params: { orgId: first.orgId } });
  },
});

const login = createRoute({
  getParentRoute: () => root,
  path: '/inloggen',
  validateSearch: (s: Record<string, unknown>): { uitnodiging?: string } => ({ uitnodiging: typeof s.uitnodiging === 'string' ? s.uitnodiging : undefined }),
  component: LoginPage,
});
const invitation = createRoute({
  getParentRoute: () => root,
  path: '/uitnodiging',
  validateSearch: (s: Record<string, unknown>): { token?: string } => ({ token: typeof s.token === 'string' ? s.token : undefined }),
  component: InvitationPage,
});
const forgot = createRoute({ getParentRoute: () => root, path: '/wachtwoord-vergeten', component: ForgotPasswordPage });
const reset = createRoute({
  getParentRoute: () => root,
  path: '/wachtwoord-nieuw',
  validateSearch: (s: Record<string, unknown>): { token?: string; error?: string } => ({
    token: typeof s.token === 'string' ? s.token : undefined,
    error: typeof s.error === 'string' ? s.error : undefined,
  }),
  component: ResetPasswordPage,
});
const signup = createRoute({ getParentRoute: () => root, path: '/aanmelden', component: SignupPage });

// Public help pages; the articles load only when someone opens them.
const helpIndex = createRoute({ getParentRoute: () => root, path: '/hulp', component: lazyRouteComponent(() => import('./routes/help'), 'HelpIndexPage') });
const helpPage = createRoute({ getParentRoute: () => root, path: '/hulp/$slug', component: lazyRouteComponent(() => import('./routes/help'), 'HelpArticlePage') });
const legalPage = createRoute({ getParentRoute: () => root, path: '/juridisch/$slug', component: lazyRouteComponent(() => import('./routes/legal'), 'LegalPage') });

const start = createRoute({
  getParentRoute: () => root,
  path: '/start',
  beforeLoad: async ({ context }) => {
    const me = await loadMe(context.queryClient);
    if (!me) throw redirect({ to: '/inloggen' });
    // One salon per account (more locations live inside it); already set up → go there.
    const first = me.salons.find((s) => !s.support);
    if (first) throw redirect({ to: '/s/$orgId/vandaag', params: { orgId: first.orgId } });
  },
  component: StartPage,
});

const salon = createRoute({
  getParentRoute: () => root,
  path: '/s/$orgId',
  beforeLoad: async ({ context, params }) => {
    const me = await loadMe(context.queryClient);
    if (!me) throw redirect({ to: '/inloggen' });
    if (!me.salons.some((s) => s.orgId === params.orgId)) throw redirect({ to: '/' });
  },
  component: AppLayout,
});

const salonIndex = createRoute({
  getParentRoute: () => salon,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/s/$orgId/vandaag', params });
  },
});
const today = createRoute({ getParentRoute: () => salon, path: '/vandaag', component: TodayPage });
const agenda = createRoute({ getParentRoute: () => salon, path: '/agenda', validateSearch: validateAgendaSearch, component: AgendaPage });
const clients = createRoute({
  getParentRoute: () => salon,
  path: '/klanten',
  validateSearch: (s: Record<string, unknown>): { klant?: string } => ({ klant: typeof s.klant === 'string' && /^[0-9a-f-]{36}$/.test(s.klant) ? s.klant : undefined }),
  component: ClientsPage,
});
const salonHub = createRoute({ getParentRoute: () => salon, path: '/salon', component: SalonHubPage });
const servicesPage = createRoute({ getParentRoute: () => salon, path: '/salon/diensten', component: ServicesPage });
const teamPage = createRoute({ getParentRoute: () => salon, path: '/salon/team', component: TeamPage });
const hoursPage = createRoute({ getParentRoute: () => salon, path: '/salon/openingstijden', component: HoursPage });
const businessPage = createRoute({ getParentRoute: () => salon, path: '/salon/bedrijf', component: BusinessPage });
const locationsPage = createRoute({
  getParentRoute: () => salon,
  path: '/salon/locaties',
  validateSearch: (s: Record<string, unknown>): { nieuw?: 1 } => ({ nieuw: s.nieuw === 1 || s.nieuw === '1' ? 1 : undefined }),
  component: LocationsPage,
});
const welcomePage = createRoute({ getParentRoute: () => salon, path: '/welkom', component: WelcomePage });
const messagesPage = createRoute({ getParentRoute: () => salon, path: '/salon/berichten', component: MessagesPage });
const bookingPage = createRoute({ getParentRoute: () => salon, path: '/salon/online-boeken', component: BookingSettingsPage });
const privacyPage = createRoute({ getParentRoute: () => salon, path: '/salon/privacy', component: PrivacyPage });
const accountPage = createRoute({ getParentRoute: () => salon, path: '/account', component: MyAccountPage });

const trialStarted = createRoute({ getParentRoute: () => root, path: '/proefperiode/gelukt', component: TrialStartedPage });
const subscriptionPage = createRoute({ getParentRoute: () => salon, path: '/salon/abonnement', component: lazyRouteComponent(() => import('./routes/subscription'), 'SubscriptionPage') });
/** Back from Plug&Pay's checkout (one fixed address): opens the owner's subscription page. */
const subscriptionReturn = createRoute({
  getParentRoute: () => root,
  path: '/abonnement/gelukt',
  beforeLoad: async ({ context }) => {
    if (!(await loadMe(context.queryClient))) throw redirect({ to: '/inloggen' });
  },
  component: lazyRouteComponent(() => import('./routes/subscription'), 'SubscriptionReturnPage'),
});

/** Back office for our own team; the API checks again on every call. */
const admin = createRoute({
  getParentRoute: () => root,
  path: '/beheer',
  beforeLoad: async ({ context }) => {
    const me = await loadMe(context.queryClient);
    if (!me) throw redirect({ to: '/inloggen' });
    if (!me.isAdmin) throw redirect({ to: '/' });
  },
  component: AdminPage,
});

const routeTree = root.addChildren([
  index,
  login,
  signup,
  invitation,
  forgot,
  reset,
  start,
  admin,
  helpIndex,
  helpPage,
  legalPage,
  trialStarted,
  subscriptionReturn,
  salon.addChildren([salonIndex, today, agenda, clients, salonHub, servicesPage, teamPage, hoursPage, businessPage, bookingPage, messagesPage, locationsPage, privacyPage, accountPage, welcomePage, subscriptionPage]),
]);

installErrorReporting('app');

/** When a page crashes: tell us, and give the person a way out instead of a blank screen. */
function PageError({ error }: { error: unknown }) {
  const t = useT();
  useEffect(() => reportError('app', error), [error]);
  return (
    <div className="flex min-h-[60dvh] items-center justify-center px-4">
      <div className="flex max-w-md flex-col items-start gap-3">
        <h1 className="page-title">{t('error.page.title')}</h1>
        <p className="text-muted">{t('error.page.body')}</p>
        <button type="button" onClick={() => location.reload()} className="mt-2 inline-flex min-h-11 items-center rounded-full bg-accent px-5 font-medium text-on-accent">
          {t('error.page.reload')}
        </button>
      </div>
    </div>
  );
}

const router = createRouter({ routeTree, context: { queryClient }, defaultPreload: 'intent', defaultErrorComponent: PageError });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
